import { UpdateEquipmentDeliveryFeeBody, UpdateEquipmentPricingBody } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { handleApiCall } from "../api-error-handler";
import { privateAxios } from "../axiosInstances";
import {
  equipmentsQueryKeys,
  eventEquipmentRequestQueryKeys,
} from "../query-keys/equipments.query-keys";
import { CreateEquipmentForm } from "../schemas";

const createEquipment = async (body: CreateEquipmentForm) => {
  try {
    const { data } = await privateAxios.post("/equipments", body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useCreateEquipmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEquipment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.lists(),
      });
    },
  });
};

export type UpdateEquipmentStocksBody = {
  name: string;
  quantity: number;
  quantityLeft: number;
};

const updateEquipmentStocks = async (equipmentId: string, data: UpdateEquipmentStocksBody) => {
  try {
    const { data: response } = await privateAxios.patch(`/equipments/${equipmentId}`, data);
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useUpdateEquipmentStocksMutation = (equipmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEquipmentStocksBody) => updateEquipmentStocks(equipmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.detail(equipmentId),
      });
    },
  });
};

const updateEquipmentPricing = async (equipmentId: string, data: UpdateEquipmentPricingBody) => {
  try {
    const { data: response } = await privateAxios.patch(`/equipments/${equipmentId}/pricing`, data);
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpdateEquipmentPricingMutation = (equipmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEquipmentPricingBody) => updateEquipmentPricing(equipmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.detail(equipmentId),
      });
    },
  });
};

const updateEquipmentDeliveryFee = (equipmentId: string, data: UpdateEquipmentDeliveryFeeBody) =>
  handleApiCall(() => privateAxios.patch(`/equipments/${equipmentId}/delivery-fee`, data));

export const useUpdateEquipmentDeliveryFeeMutation = (equipmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEquipmentDeliveryFeeBody) =>
      updateEquipmentDeliveryFee(equipmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: equipmentsQueryKeys.detail(equipmentId),
      });
    },
  });
};

export type RequestEquipmentsBody = {
  exhibitorId?: string;
  equipments: Array<{
    eventEquipmentId: string;
    count: number;
    dates?: string[];
  }>;
};

const requestEquipments = async (eventId: string, body: RequestEquipmentsBody) => {
  try {
    const { data } = await privateAxios.post(`/equipments/requests/${eventId}`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useRequestEquipmentsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RequestEquipmentsBody) => requestEquipments(eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEquipmentRequestQueryKeys.list(eventId),
      });
    },
  });
};

const handleEquipmentsRequest = async (
  requestId: string,
  status: "accept" | "refuse",
  price?: number | null,
) => {
  try {
    const { data } = await privateAxios.patch(`/equipments/requests/${requestId}/${status}`, {
      price,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useHandleEquipmentsRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      status,
      price,
    }: {
      requestId: string;
      status: "accept" | "refuse";
      price?: number | null;
    }) => handleEquipmentsRequest(requestId, status, price),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEquipmentRequestQueryKeys.lists(),
      });
    },
  });
};

const payEquipmentsRequest = async (requestId: string) => {
  try {
    const { data } = await privateAxios.patch(`/equipments/requests/${requestId}/pay`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const usePayEquipmentsRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => payEquipmentsRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEquipmentRequestQueryKeys.lists(),
      });
    },
  });
};
